import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';

export type ModuleEventName =
  | 'actividad'
  | 'combustible'
  | 'fatiga'
  | 'checklist'
  | 'firma-digital'
  | 'seguimiento-anonimo'
  | 'auth'
  | 'store-forward'
  | 'conectividad'
  | 'viajes';

export interface ModuleEvent {
  module: ModuleEventName;
  action: string;
  payload?: Record<string, unknown>;
  timestamp: string;
}

export type ModuleEventSink = (event: ModuleEvent) => void;

const SENSITIVE_KEYS = ['signature', 'firma', 'token', 'password', 'key'];

@Injectable({ providedIn: 'root' })
export class EventLogService {
  private sinks: ModuleEventSink[] = [];

  registerSink(sink: ModuleEventSink): () => void {
    this.sinks.push(sink);
    return () => {
      this.sinks = this.sinks.filter((s) => s !== sink);
    };
  }

  log(
    module: ModuleEventName,
    action: string,
    payload?: Record<string, unknown>
  ): void {
    const event: ModuleEvent = {
      module,
      action,
      payload: payload ? this.sanitizePayload(payload) : undefined,
      timestamp: new Date().toISOString(),
    };

    if (!environment.production) {
      console.info('[ModuleEvent]', event);
    }

    for (const sink of this.sinks) {
      try {
        sink(event);
      } catch {
        // sink failures must not break app flow
      }
    }
  }

  private sanitizePayload(
    payload: Record<string, unknown>
  ): Record<string, unknown> {
    const out: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(payload)) {
      const lower = key.toLowerCase();
      if (SENSITIVE_KEYS.some((s) => lower.includes(s))) {
        out[key] = '[redacted]';
      } else if (typeof value === 'string' && value.startsWith('data:image/')) {
        out[key] = '[image]';
      } else {
        out[key] = value;
      }
    }
    return out;
  }
}
